import { IMicroConfiguration } from '../types/configuration';
import { TViewRouteTable } from '../transformations/view_route_table';
import { ViewStateProcess } from './view_state';
import { ViewModelProcess } from './view_model';

import { IRoute, IRouteRequest, TParameters } from'../../../dip/library/di-hnf/di-hnf.js';
import { Process } from'../../../pip/library/pi-interop/pi-interop.js';
import ess from'../../../pip/library/pi-ess/pi-ess.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';
import Navigo from 'navigo';

type TNavigoRouteHandler =
  | ((params: TParameters, query: string) => void)
  | { as: string; uses(params: TParameters, query: string): void };

export type TRouterConfiguration = {
  useHash: true;
};

export class RoutingProcess extends Process {
  id = 'routing';

  dependencies = ['/view_state', '/view_model'];
  processes: {
    view_state: ViewStateProcess;
    view_model: ViewModelProcess;
  };

  configuration: IMicroConfiguration;

  routeHandlers: Record<string, TNavigoRouteHandler>;
  router: Navigo;

  protected _activeRoute: IRouteRequest;

  /**
   *
   * @param configuration
   */
  initialize(configuration: IMicroConfiguration) {
    this.configuration = configuration;

    this.router = new Navigo(null, true, '#');
    this.router.notFound((query: string) => this.__onNotFound(query));
    return f1.done(this);
  }

  activate() {
    return new Promise<this>((resolve) => {
      resolve(this);
    });
  }

  deactivate() {
    const router = this.router;

    router.pause();
    router.destroy();
    return f1.done(this);
  }

  get activeRoute() {
    return this._activeRoute;
  }

  addRouteTable(routerTable: TViewRouteTable) {
    const router = this.router;
    const routes = f1.to.array(routerTable);

    // @todo remove route handles if they exist
    this.routeHandlers = {};
    for (const route of routes) {
      this.routeHandlers[route.abs_uri] = (parameters: TParameters) => {
        this.__onRoute(parameters, route);
      };

      router.on(route.abs_uri, this.routeHandlers[route.abs_uri], {});
      ess.trap({
        status: 'info',
        site: 'micro',
        source: this.id,
        action: 'register-route',
        data: route
      });
    }

    router.resolve();
  }

  private __onRoute(parameters: TParameters, route: IRoute) {
    const { url: uri } = this.router.lastRouteResolved();
    const request = (this._activeRoute = this.__routerState(uri, parameters, route));

    this.toRoute(request);
  }

  private __onNotFound(uri: string) {
    this.to404(uri);
  }

  /**
   * update the Micro state & view with a new state object
   */
  toRoute(route: IRouteRequest) {
    ess.info({
      site: 'micro',
      source: this.id,
      action: 'request-route',
      data: route
    });

    return this.processes.view_state.transitionTo(route);
  }

  /**
   * if the current route is not in a valid path for this micro, then navigate to the 404 path
   * @note we always route to #/404 and let anything at that state handle the issue
   */
  to404(requestedURI: string) {
    const microBaseURI = `#${this.configuration.routes.uri}`;
    const currentURI = location.hash;

    const routeUnderMicro = currentURI.indexOf(microBaseURI) === 0 && currentURI.length > microBaseURI.length;
    if (routeUnderMicro) {
      ess.failure({
        site: 'micro',
        source: this.id,
        action: 'request-404',
        data: {
          requestedURI,
          currentURI
        }
      });
    }
  }

  private __routerState(uri: string, parameters: TParameters, routerRoute: IRoute): IRouteRequest {
    return {
      state: routerRoute.abs_state,
      parameters: parameters || {},
      uri: uri || '',
      ts: Date.now()
    };
  }
}
